<template>
  <draggable
    :options="{disabled: disabled || isSaving}"
    v-model="updatedRoster"
    @change="saveRoster()"
  >
    <v-row
      v-for="(item, i) in firstFiveSlots()"
      :key="i"
      align="center"
    >
      <v-col cols="auto">
        <v-icon class="drag-icon">mdi-drag-horizontal-variant</v-icon>
      </v-col>
      <v-col cols="auto">
        <b>{{ i + 1 }}</b>
      </v-col>
      <v-col class="roster-item-input">
        <v-autocomplete
          :loading="!vendorOptions"
          outlined
          hide-details
          :items="vendorOptions"
          item-text="name"
          item-value="id"
          :value="item.vendorId || item.preferenceType"
          return-object
          :append-icon="shouldShowLink(item) ? 'mdi-launch' : undefined"
          @click:append="shouldShowLink(item) ? $emit('goToLink', item.vendorId) : undefined"
          :disabled="disableRosterSlot(i)"
          @change="saveInput($event, i)"
        >
          <template v-slot:selection="{ item }">
            <span v-if="item.name">{{item.name}}</span>
            <span v-if="item.invalidVendorName" class="red--text">{{item.invalidVendorName}}</span>
          </template>
          <template v-slot:item="{ item, attrs }">
            <span v-if="item.name">{{item.name || 'Not'}}</span>
            <span v-else :class="{'text--disabled': attrs.disabled}">Selected Vendor Is Not Available</span>
          </template>
        </v-autocomplete>
        <v-chip
          v-if="item.vendor && item.vendor.preferenceType === 'Internal Technician'"
          class="handyman-badge"
          color="primary" small
        >Internal Tech</v-chip
        >
      </v-col>
      <v-col class="manage-column">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
          <span v-on="on">
            <EsButtonIcon
              :disabled="disabled || isSaving"
              icon="mdi-plus-box-multiple"
              aria-label="Manage All Categories"
              v-if="item && item.preferenceType === 'vendor' && !assetId && isValidVendor(item.vendorId)"
              @click="emitOpenAssignDialog(item)" />
          </span>
          </template>
          <span>Manage All Categories</span>
        </v-tooltip>
      </v-col>
      <v-col class="auto-dispatch-column">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
      <span v-on="on">
        <EsButtonIcon
          :disabled="true"
          icon="mdi-auto-fix"
          aria-label="Auto-Dispatch"
          v-if="isAutoDispatchInternalTech(item.vendorId)" />
      </span>
          </template>
          <span>Auto-Dispatch</span>
        </v-tooltip>
      </v-col>
      <v-col class="contract-column">
        <v-tooltip bottom v-if="i === 0">
          <template v-slot:activator="{ on }">
            <v-row dense align="center" v-on="on">
              <v-col cols="auto">
                <v-icon aria-label="Contract Bound" :disabled="disabled || isSaving">mdi-file-document-edit</v-icon>
              </v-col>
              <v-col cols="auto">
                <v-checkbox
                  :disabled="disabled || isSaving"
                  v-model="item.contractBound"
                  @change="setContractBound($event, i)"
                ></v-checkbox>
              </v-col>
            </v-row>
          </template>
          <span>Contract Bound</span>
        </v-tooltip>
      </v-col>
    </v-row>
  </draggable>
</template>

<script>
import Draggable from "vuedraggable";
import { EsButtonIcon } from "@86-repairs/design-system";

export default {
  name: 'EsVendorRosterSlots',
  components: {
    Draggable,
    EsButtonIcon,
  },
  props: {
    roster: {
      type: Array,
      default: () => ([]),
    },
    assetId: {
      type: String,
    },
    categoryId: {
      type: String,
    },
    vendorsForArea: {
      type: Array,
      default: () => ([]),
    },
    isSsp: {
      type: Boolean,
      default: false,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    updatedRoster: [],
    vendorOptions: null,
  }),
  mounted() {
    this.updatedRoster = [...this.roster];
    this.setEligibleVendors();
  },
  watch: {
    roster() {
      this.updatedRoster = [...this.roster];
      this.setEligibleVendors();
    },
  },
  methods: {
    firstFiveSlots() {
      return this.updatedRoster.slice(0, 5);
    },
    shouldShowLink(item) {
      return !this.isSsp && (item.preferenceType === "vendor");
    },
    disableRosterSlot(index) {
      const indexOfDoNotUse = this.updatedRoster.findIndex(
        (item) => item.preferenceType === "no_one_else" || item.contractBound
      );
      return (
        (this.updatedRoster[0]?.preferenceType === "landlord" && index !== 0) ||
        (indexOfDoNotUse !== -1 && index > indexOfDoNotUse) ||
        this.isSaving || this.disabled
      );
    },
    setContractBound(event, index) {
      this.updatedRoster[index].contractBound = event;
      this.saveRoster();
    },
    saveInput(option, index) {
      if (option.preferenceType === 'vendor') {
        this.updatedRoster[index] = {
          'vendorId': option.id,
          'preferenceType': option.preferenceType,
        };
      } else {
        this.updatedRoster[index] = {
          'preferenceType': option.preferenceType
        };
      }
      this.saveRoster();
    },
    saveRoster() {
      const useNoOneElseIndex = this.updatedRoster.findIndex((item) => item.preferenceType === "no_one_else");
      if (useNoOneElseIndex > -1) {
        this.updatedRoster = this.updatedRoster.fill({preferenceType: "eightysix_mix"}, useNoOneElseIndex + 1, this.updatedRoster.length);
      }

      const payload = [{ roster: this.updatedRoster }];
      payload[0].categoryId = this.categoryId;
      payload[0].assetId = this.assetId;
      this.$emit('saveRoster', payload);
    },
    resetVendorOptions() {
      this.vendorOptions = [
        { name: "Landlord", id: "landlord", preferenceType: "landlord" },
        { name: "86 Mix", id: "eightysix_mix", preferenceType: "eightysix_mix" },
        { name: "Use No One Else", id: "no_one_else", preferenceType: "no_one_else" },
      ]
    },
    isValidVendor(vendorId) {
      const vendorHasMultipleServices = this.vendorsForArea.find((vendor) => vendor.id === vendorId)?.serviceCategoryIds?.length > 1;
      const allVendorIds = this.vendorsForArea.map((vendor) => vendor.id);
      return vendorHasMultipleServices && !!allVendorIds.includes(vendorId);
    },
    isAutoDispatchInternalTech(vendorId) {
      const vendor = this.vendorsForArea.find((vendor) => vendor.id === vendorId);
      return vendor && vendor.vendorAutomationOptions && vendor.vendorAutomationOptions.internalTechAutoDispatch;
    },
    setEligibleVendors() {
      this.resetVendorOptions();
      const currentSelections = [...this.roster].filter(
        (item) =>
          item.preferenceType === "vendor"
      ).map((item) => {
        return {
          id: item.vendorId,
          preferenceType: 'vendor',
          invalidVendorName: item.vendor?.name || 'Selected Vendor Is Not Available'
        }
      });
      const vendorsForCategory = this.vendorsForArea
        .filter((item) => item.serviceCategoryIds.includes(this.categoryId))
        .map((item) => {
          return {
            name: item.name,
            id: item.id,
            preferenceType: 'vendor',
          };
        });
      const eligibleOptions = this.vendorOptions
        .concat(vendorsForCategory)
        .concat(currentSelections);
      const distinct = [];
      const newMap = new Map();
      for (const item of eligibleOptions) {
        if (!item.name) {
          item.disabled = true;
        }
        if (!newMap.has(item.id)) {
          newMap.set(item.id, true);
          distinct.push(item);
        }
      }
      this.vendorOptions = distinct;
    },
    emitOpenAssignDialog(item) {
      let vendorObject = {};
      if (item.vendor) {
        vendorObject = { vendorId: item.vendorId, vendorName: item.vendor.name };
      } else {
        const vendor = this.vendorsForArea.find((vendor) => vendor.id === item.vendorId);
        vendorObject = { vendorId: vendor.id, vendorName: vendor.name }
      }
      this.$emit('openAssignDialog', vendorObject);
    }
  }
};
</script>

<style scoped>
.manage-column {
  max-width: 50px;
}
.auto-dispatch-column {
  max-width: 50px;
  padding-right: 60px;
}
.contract-column {
  max-width: 100px;
}
.drag-icon {
  opacity: 0.5;
  cursor: grab;
}
.roster-item-input {
  position: relative;
}
.handyman-badge {
  position: absolute;
  top: 0px;
  right: 55px;
}
</style>
